/**
 * @fileoverview Shopping Cart Utility Functions
 *
 * This file contains pure utility functions used throughout the shopping cart
 * component system for calculations, data transformations, and UI helpers.
 *
 * Core Utilities:
 * - Cart Calculations: Total price and item count calculations
 * - Data Transformations: Converting API responses to UI-friendly formats
 * - Array Operations: Sorting, filtering, and updating cart items
 * - UI Helpers: Body scroll management, loading button content
 * - Form Processing: Checkout payload formatting
 *
 * Key Features:
 * - Pure functions with no side effects for predictable behavior
 * - Comprehensive data transformation between API and UI formats
 * - Alphabetical sorting for consistent cart item display
 * - React element creation for loading states
 * - Proper cleanup functions for DOM manipulation
 */

import { ShoppingCartItem, BasketListItem } from "./ShoppingCart.types";
import { Circle } from "better-react-spinkit";
import { SHOP_CONFIG } from "pages/Shop/ShopList.config";
import React from "react";

/**
 * Calculates the total price of all items in the cart
 * @param items - Array of cart items
 * @returns Total price as a number
 */
export const calculateTotal = (items: ShoppingCartItem[]): number => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
};

/**
 * Extracts currency symbol from cart items, using the first available symbol
 * or falling back to the configured default
 * @param items - Array of cart items
 * @returns Currency symbol string
 */
export const getCurrencySymbolFromItems = (items: ShoppingCartItem[]): string => {
  const itemWithCurrency = items.find(
    (item) => item.currencySymbol && item.currencySymbol.trim() !== ""
  );
  return itemWithCurrency?.currencySymbol || SHOP_CONFIG.FALLBACK_CURRENCY_SYMBOL;
};

/**
 * Comprehensive currency symbol extraction that handles multiple data sources
 * Priority order: checkout data details > cart items > fallback config
 * @param items - Array of cart items (from basket list or checkout)
 * @param checkoutData - Optional checkout data containing global currency info
 * @returns Currency symbol string
 */
export const getCurrencySymbol = (items: ShoppingCartItem[], checkoutData?: any): string => {
  // First priority: Check if checkout data has a global currency symbol
  if (checkoutData?.details?.currencySymbol && checkoutData.details.currencySymbol.trim() !== "") {
    return checkoutData.details.currencySymbol;
  }

  // Second priority: Extract from cart items (for basket list endpoint)
  const itemWithCurrency = items.find(
    (item) => item.currencySymbol && item.currencySymbol.trim() !== ""
  );
  if (itemWithCurrency?.currencySymbol) {
    return itemWithCurrency.currencySymbol;
  }

  // Final fallback: Use configured default
  return SHOP_CONFIG.FALLBACK_CURRENCY_SYMBOL;
};

/**
 * Gets the total number of items in the cart (sum of all quantities)
 * @param items - Array of cart items
 * @returns Total item count as a number
 */
export const getTotalItemCount = (items: ShoppingCartItem[]): number => {
  return items.reduce((count, item) => count + item.quantity, 0);
};

/**
 * Updates the quantity of a specific item in the cart array
 * @param items - Array of cart items
 * @param itemId - ID of the item to update
 * @param newQuantity - New quantity for the item
 * @returns New array with updated item quantity
 */
export const updateItemQuantity = (
  items: ShoppingCartItem[],
  itemId: number,
  newQuantity: number
): ShoppingCartItem[] => {
  return items.map((item) => (item.id === itemId ? { ...item, quantity: newQuantity } : item));
};

/**
 * Removes an item from the cart array
 * @param items - Array of cart items
 * @param itemId - ID of the item to remove
 * @returns New array without the specified item
 */
export const removeItem = (items: ShoppingCartItem[], itemId: number): ShoppingCartItem[] => {
  return items.filter((item) => item.id !== itemId);
};

/**
 * Converts a BasketListItem from the API to a ShoppingCartItem for the component
 * @param basketItem - Raw basket item from API response
 * @param globalCurrencySymbol - Optional global currency symbol from checkout context
 * @returns Formatted cart item for component use
 */
export const convertBasketItemToCartItem = (
  basketItem: BasketListItem,
  globalCurrencySymbol?: string
): ShoppingCartItem => {
  return {
    id: basketItem.catalogueItems__Id,
    name: basketItem.catalogueItems__ItemsName,
    itemCode: basketItem.catalogueItems__Code,
    itemDescription: basketItem.catalogueItems__Description,
    itemImageUrl: basketItem.catalogueItems__ImageFilePath,
    price: basketItem.catalogueItems__Price,
    quantity: basketItem.shopBasket__Quantity,
    basketId: basketItem.shopBasket__Id, // Include basket ID for API operations
    currencySymbol: basketItem.currencySymbol || globalCurrencySymbol,
    total: basketItem.total,
  };
};

/**
 * Manages body scroll prevention when cart is open
 * @param isOpen - Whether the cart is currently open
 * @returns Cleanup function to restore body scroll
 */
export const manageBodyScroll = (isOpen: boolean): (() => void) => {
  if (isOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  // Return cleanup function
  return () => {
    document.body.style.overflow = "";
  };
};

/**
 * Sorts cart items alphabetically by name
 * @param items - Array of cart items to sort
 * @returns New sorted array of cart items
 */
export const sortCartItemsByName = (items: ShoppingCartItem[]): ShoppingCartItem[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Extracts and converts cart items from checkout API response
 * @param checkoutData - Raw checkout data from API
 * @returns Array of formatted cart items
 */
export const extractCartItemsFromCheckoutData = (checkoutData: any): ShoppingCartItem[] => {
  if (!checkoutData?.details?.listData || !Array.isArray(checkoutData.details.listData)) {
    console.warn("Invalid checkout data structure for cart items extraction");
    return [];
  }

  // Extract global currency symbol from checkout details if available
  const globalCurrencySymbol = checkoutData.details?.currencySymbol;

  const cartItems = checkoutData.details.listData.map((item: BasketListItem) =>
    convertBasketItemToCartItem(item, globalCurrencySymbol)
  );
  return sortCartItemsByName(cartItems);
};

/**
 * Formats checkout form data into the POST payload structure
 * @param formData - Form data from checkout form
 * @returns Formatted payload for order submission
 */
export const formatCheckoutPayload = (formData: Record<string, any>): any => {
  const payload = {
    sitesId: formData.SitesId || null,
    sitesName: formData.SitesName || null,
    addressLine1: formData.AddressLine1 || null,
    addressLine2: formData.AddressLine2 || null,
    addressLine3: formData.AddressLine3 || null,
    addressLine4: formData.AddressLine4 || null,
    city: formData.City || null,
    stateProvince: formData.StateProvince || null,
    usStatesID: formData.USStatesID || null,
    postalZipCode: formData.PostalZipCode || null,
    countriesId: formData.CountriesId || null,
    listOfSites: [],
    listOfUSStates: [],
    listOfCountries: [],
    total: null,
    listData: [],
  };

  return payload;
};

/**
 * Creates a loading button content with spinner and text
 * @param text - Loading text to display
 * @param size - Size of the spinner (default: 20)
 * @param color - Color of the spinner (default: "#ffffff")
 * @returns JSX element with spinner and text
 */
export const createLoadingButtonContent = (
  text: string,
  size: number = 20,
  color: string = "#ffffff"
): React.ReactElement => {
  return React.createElement(
    "div",
    { style: { display: "flex", alignItems: "center", justifyContent: "center" } },
    React.createElement(Circle, { size, color, scaleStart: 0.4, scaleEnd: 0.8 }),
    React.createElement("span", { style: { marginLeft: "8px" } }, text)
  );
};
